// @flow
import React, { Component } from 'react';
import styled from 'styled-components';
import { translate } from 'react-i18next';
import { Dialog } from 'common/components/ui/Dialog';
import RaisedButton from 'material-ui/RaisedButton';
import TextField from 'material-ui/TextField';
import moment from 'moment';
import DateRange from 'common/domain/DateRange';
import DateRangePicker from 'common/components/DateRangePicker';
import Proposition, { PropositionCustomer } from 'common/modules/proposition/domain/proposition';
import Estimation from 'common/modules/proposition/domain/estimation';
import CustomerSelect from 'statsradio/modules/customers/components/CustomerSelect';
import RadioCustomer from 'statsradio/modules/customers/types/RadioCustomer';
import AddCustomerDialog from 'statsradio/modules/customers/components/AddCustomerDialog';
import SalesEstimationBriefSummary from '../SalesEstimationBriefSummary';

const Wrapper = styled.div`
`;

const Title = styled.h4`
  margin-top: ${({ theme }) => theme.metrics.margin + theme.metrics.smallMargin}px;
  padding: 0;
`;

const DateRangeWrapper = styled.div`
  flex: 3;

  display: flex;
  flex-direction: row;
  align-items: center;

  align-self: start;
`;

const CreateCustomerText = styled.div`
  font-size: ${({ theme }) => theme.fonts.style.midSmall.fontSize}px;
  margin-left: ${({ theme }) => theme.metrics.margin}px;
  text-decoration: underline;
  cursor: pointer;
`;

const DropDownWrapper = styled.div`
  min-width: 200px;
  margin-top: ${({ theme }) => theme.metrics.smallMargin}px;
  display: flex;
  align-items: center;
`;

type DefaultProps = {
  shouldHaveAllInformations: boolean,
  error: boolean,

  onClose: () => void,
  onPropose: (newProposition: Proposition) => void,

  actions: {
    fetchCustomers: () => any,
  },
  t: any,
}

type WithEstimation = DefaultProps & { estimation: Estimation, proposition: void };
type WithProposition = DefaultProps & { proposition: Proposition, estimation: void };

type Props =
| WithEstimation
| WithProposition;

type State = {
  title: string,
  description: string,
  notes: string,
  customer: ?PropositionCustomer,
  dateRange: DateRange,
  showAddCustomerDialog: boolean;
};

const DEFAULT_STATE: State = {
  title: '',
  description: '',
  notes: '',
  customer: undefined,
  dateRange: DateRange.default(),
  showAddCustomerDialog: false,
};

class SavePropositionDialog extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { proposition, estimation } = this.props;

    this.state = {
      ...DEFAULT_STATE,
      dateRange: estimation ? estimation.getDateRange() : DEFAULT_STATE.dateRange,
    };

    if (proposition) {
      this.state = {
        ...this.state,
        title: proposition.title,
        description: proposition.description || '',
        notes: proposition.notes || '',
        customer: proposition.customer,
        dateRange: proposition.dateRange || DateRange.default(),
      };
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const { estimation, proposition } = nextProps;

    if (proposition) {
      this.setState({
        title: proposition.title,
        description: proposition.description || '',
        notes: proposition.notes || '',
        customer: proposition.customer,
        dateRange: proposition.dateRange || DateRange.default(),
      });
    } else if (estimation) {
      this.setState({
        dateRange: estimation.getDateRange(),
      });
    }
  }

  onOpenAddCustomerDialog = () => {
    this.setState({ showAddCustomerDialog: true });
  }

  onCloseAddCustomerDialog = (customer?: RadioCustomer) => {
    const propositionCustomer = customer && this.parseToPropositionCustomer(customer);

    this.setState((prevState) => {
      return {
        showAddCustomerDialog: false,
        customer: propositionCustomer || prevState.customer,
      };
    });
  }

  parseToPropositionCustomer = (customer: RadioCustomer): PropositionCustomer => ({
    reference: customer.getReference(),
    name: customer.getAdvertiserName(),
    hasAccessToFollo: customer.hasAccessToFollo,
  });

  onClose = () => {
    this.props.onClose();
    this.setState(DEFAULT_STATE);
  };

  onSave = () => {
    const { title, description, notes, customer, dateRange } = this.state;

    let estimation = this.props.estimation;
    if (!estimation && this.props.proposition && this.props.proposition.estimation) {
      estimation = this.props.proposition.estimation;
    }

    if (!estimation) {
      this.onClose();
      return;
    }

    if (!this.isPropositionValid()) {
      return;
    }

    const proposition = this.props.proposition;
    this.props.onPropose(new Proposition({
      campaignReference: proposition ? proposition.campaignReference : '',
      radio: proposition ? proposition.radio : null,
      totalCost: proposition ? proposition.totalCost : 0,
      state: proposition ? proposition.state : 'DRAFT',
      salesman: proposition ? proposition.salesman : null,
      reference: proposition ? proposition.reference : '',
      proposedOn: proposition ? proposition.proposedOn : estimation.estimatedOn,
      title,
      description,
      notes,
      customer,
      dateRange: dateRange,
      estimation,
    }));

    this.onClose();
  }

  onCustomerChanged = (customer: RadioCustomer) => {
    this.setState({ customer: this.parseToPropositionCustomer(customer) });
  }

  onTitleChange = (event: any, title: string) => {
    this.setState({ title });
  }

  onDescriptionChange = (event: any, description: string) => {
    this.setState({ description });
  }

  onNotesChange = (event: any, notes: string) => {
    this.setState({ notes });
  }

  onDateRangeChange = ({ from, to }: { from: moment, to: moment }): void => {
    this.setState({ dateRange: new DateRange({ from, to }) });
  }

  isPropositionValid = (): boolean => {
    if (!this.props.shouldHaveAllInformations) {
      return Boolean(this.state.title);
    }
    const { title, customer, dateRange } = this.state;
    return Boolean(title && customer && dateRange);
  }

  render() {
    const { shouldHaveAllInformations, t } = this.props;
    const titleError = this.state.title ? undefined : t('requiredTitle');
    const customerError = this.state.customer || !shouldHaveAllInformations ? undefined : t('requiredCustomer');
    const estimation = this.props.proposition ? this.props.proposition.estimation : this.props.estimation;
    const isProposed = this.props.proposition ? this.props.proposition.isProposed() : false;

    let saveLabel;
    let title;
    if (shouldHaveAllInformations) {
      saveLabel = t('send');
      title = t('sendProposition');
    } else if (this.props.proposition) {
      saveLabel = t('modify');
      title = t('modifyProposition');
    } else {
      saveLabel = t('saveProposition');
      title = t('createProposition');
    }

    const dialogActions = [
      <RaisedButton label={t('cancel')} onClick={this.onClose} key="cancel" />,
      <RaisedButton primary label={saveLabel} onClick={this.onSave} disabled={!this.isPropositionValid()} key="save" />,
    ];

    return (
      <Dialog
        title={title}
        open={Boolean(estimation)}
        onClose={this.onClose}
        actions={dialogActions}
      >
        <Wrapper>
          <Title>{t('selectCustomer')}</Title>
          <DropDownWrapper>
            <CustomerSelect
              value={this.state.customer && this.state.customer.reference}
              disabled={isProposed}
              onCustomerChanged={this.onCustomerChanged}
              customerError={customerError}
              inviteCustomer={false}
            />
            {!isProposed &&
            <CreateCustomerText onClick={this.onOpenAddCustomerDialog}>+ {t('customers:createACustomer')}</CreateCustomerText>
              }
          </DropDownWrapper>

          <Title>{t('campaignDates')}</Title>
          <DateRangeWrapper>
            <DateRangePicker
              weekOnly
              from={this.state.dateRange.from}
              to={this.state.dateRange.to}
              onDateRangeChange={this.onDateRangeChange}
              labelStyle={{ fontSize: 15, fontWeight: 0, paddingLeft: 0 }}
            />
          </DateRangeWrapper>

          <Title>{t('title')}</Title>
          <TextField id="title" errorText={titleError} onChange={this.onTitleChange} value={this.state.title} />

          <Title>{t('description')}</Title>
          <TextField
            id="description"
            multiLine
            onChange={this.onDescriptionChange}
            style={{ width: '100%' }}
            value={this.state.description}
          />

          <Title>{t('estimation')}</Title>
          <SalesEstimationBriefSummary estimations={[estimation]} />

          <Title>{t('commonSales:notes')}</Title>
          <TextField
            id="notes"
            multiLine
            onChange={this.onNotesChange}
            style={{ width: '100%' }}
            value={this.state.notes}
          />
        </Wrapper>
        <AddCustomerDialog visible={this.state.showAddCustomerDialog} onClose={this.onCloseAddCustomerDialog} onAddCustomer={this.onCloseAddCustomerDialog} />
      </Dialog>
    );
  }
}

export default translate('sales', 'commonSales', 'customers')(SavePropositionDialog);



// WEBPACK FOOTER //
// ./src/statsradio/modules/sales/components/proposition/SavePropositionDialog.js